import { CheckOutlined, EyeInvisibleOutlined, MessageOutlined } from "@ant-design/icons";
import { App, Button, Form, Input, Modal, Space } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { onModalOk } from "@/shared/lib";

import { useModerateComment } from "../model/useModerateComment";

type Dialog = "none" | "hide" | "delete" | "reply";

/**
 * Действия над отзывом в очереди модерации (доработки §6.2).
 *
 * **Причина обязательна у скрытия и удаления, но не у публикации.** §6.2
 * требует объяснения именно для скрытия, и это не формальность: оператор
 * накладывает ограничение, а публикация его снимает. Требовать объяснение за
 * снятие подозрения значит сделать восстановление дороже скрытия — и получить
 * очередь, которую никто не разгребает.
 *
 * Кнопки доступны по текущему статусу, а не по роли: «Опубликовать» на уже
 * опубликованном отзыве бэкенд отвергнет конфликтом, и предлагать эту кнопку
 * значит учить оператора игнорировать ошибки.
 */
export function CommentActions({
  id,
  status,
  authorKind,
}: {
  id: number;
  status: string;
  authorKind: string;
}) {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const { publish, hide, remove, reply } = useModerateComment(id);
  const [dialog, setDialog] = useState<Dialog>("none");
  const [hideForm] = Form.useForm<{ comment: string }>();
  const [deleteForm] = Form.useForm<{ comment: string }>();
  const [replyForm] = Form.useForm<{ text: string }>();

  const close = () => {
    setDialog("none");
  };

  const confirmPublish = () => {
    void modal.confirm({
      title: t("comments.publish"),
      content: t("comments.publishConfirm"),
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
      onOk: async () => {
        await publish.mutateAsync({});
        void message.success(t("comments.published"));
      },
    });
  };

  // Ответ поддержки цепляется к отзыву, а не к другому ответу: на бэкенде
  // глубина ровно 1, и кнопка на ответе вела бы прямиком в 409.
  const isReply = authorKind === "support";

  return (
    <Space wrap>
      {status === "published" || status === "deleted" ? null : (
        <Button
          type="primary"
          icon={<CheckOutlined aria-hidden />}
          loading={publish.isPending}
          onClick={confirmPublish}
        >
          {t("comments.publish")}
        </Button>
      )}

      {status === "hidden" || status === "deleted" ? null : (
        <Button
          icon={<EyeInvisibleOutlined aria-hidden />}
          loading={hide.isPending}
          onClick={() => {
            setDialog("hide");
          }}
        >
          {t("comments.hide")}
        </Button>
      )}

      {isReply || status === "deleted" ? null : (
        <Button
          icon={<MessageOutlined aria-hidden />}
          loading={reply.isPending}
          onClick={() => {
            setDialog("reply");
          }}
        >
          {t("comments.reply")}
        </Button>
      )}

      {status === "deleted" ? null : (
        <Button
          danger
          loading={remove.isPending}
          onClick={() => {
            setDialog("delete");
          }}
        >
          {t("comments.delete")}
        </Button>
      )}

      <Modal
        open={dialog === "hide"}
        title={t("comments.hide")}
        okText={t("comments.hide")}
        cancelText={t("common.cancel")}
        okButtonProps={{ danger: true, loading: hide.isPending }}
        onCancel={close}
        onOk={onModalOk(hideForm, async (values) => {
          await hide.mutateAsync(values);
          void message.success(t("comments.hidden"));
          hideForm.resetFields();
          close();
        })}
        destroyOnHidden
      >
        <Form form={hideForm} layout="vertical" requiredMark={false}>
          <Form.Item
            name="comment"
            label={t("common.comment")}
            extra={t("comments.hideHint")}
            rules={[{ required: true, min: 3, message: t("common.commentRequired") }]}
          >
            <Input.TextArea rows={3} autoFocus maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={dialog === "delete"}
        title={t("comments.delete")}
        okText={t("comments.delete")}
        cancelText={t("common.cancel")}
        okButtonProps={{ danger: true, loading: remove.isPending }}
        onCancel={close}
        onOk={onModalOk(deleteForm, async (values) => {
          await remove.mutateAsync(values);
          void message.success(t("comments.deleted"));
          deleteForm.resetFields();
          close();
        })}
        destroyOnHidden
      >
        <Form form={deleteForm} layout="vertical" requiredMark={false}>
          <Form.Item
            name="comment"
            label={t("common.comment")}
            extra={t("comments.deleteHint")}
            rules={[{ required: true, min: 3, message: t("common.commentRequired") }]}
          >
            <Input.TextArea rows={3} autoFocus maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={dialog === "reply"}
        title={t("comments.reply")}
        okText={t("comments.send")}
        cancelText={t("common.cancel")}
        okButtonProps={{ loading: reply.isPending }}
        onCancel={close}
        onOk={onModalOk(replyForm, async (values) => {
          await reply.mutateAsync(values);
          void message.success(t("comments.replied"));
          replyForm.resetFields();
          close();
        })}
        destroyOnHidden
      >
        <Form form={replyForm} layout="vertical" requiredMark={false}>
          <Form.Item
            name="text"
            label={t("comments.replyLabel")}
            extra={t("comments.replyHint")}
            rules={[{ required: true, min: 2, message: t("common.commentRequired") }]}
          >
            <Input.TextArea rows={4} autoFocus maxLength={2000} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
